<template>
  <div class="dropdown-container">
    <div class="dropdown-trigger" @click="dropdownOpen = !dropdownOpen" :class="{ active: dropdownOpen }">
      <div class="dropdown-label">
        <span class="label-text">{{ title }}</span>
        <span class="selected-count" v-if="selectedCount > 0 && !isSingle">
          ({{ selectedCount }})
        </span>
      </div>
      <div class="arrow-container">
        <svg class="arrow" :class="{ open: dropdownOpen }" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"
            stroke-linejoin="round" />
        </svg>
      </div>
    </div>

    <Transition name="dropdown">
      <div v-if="dropdownOpen" class="dropdown-menu">
        <div class="dropdown-items">

          <!-- Loading State -->
          <div v-if="itemsRef.length === 0" class="loading-container">
            <div class="loading-spinner">
              <svg class="spinner" width="20" height="20" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                  stroke-dasharray="60" stroke-dashoffset="60" />
              </svg>
            </div>
          </div>

          <label v-for="item in itemsRef" :key="item.name" class="dropdown-item" :class="{ checked: item.value }">
            <div class="checkbox-container">
              <input type="checkbox" :checked="item.value" @change.stop="updateColumnVisibility(item, !item.value)"
                class="checkbox-input" />
              <div class="custom-checkbox">
                <svg class="checkmark" width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6L5 9L10 3" stroke="white" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
                </svg>
              </div>
            </div>
            <span class="item-text">{{ item.name }}</span>
          </label>
        </div>
      </div>
    </Transition>

  </div>
</template>

<script setup>
import { ref, toRaw, computed, onMounted, onUnmounted } from 'vue';


const props = defineProps({
  title: {
    type: String,
    required: true
  },
  emitEvent: {
    type: String,
    required: true
  },
  items: {
    type: Array,
    required: true,
  },
  isSingle: {
    type: Boolean,
    required: false,
    default: false
  }
})
const emit = defineEmits()

const itemsRef = computed(() => props.items);
const dropdownOpen = ref(false);

const selectedCount = computed(() => {
  return itemsRef.value.filter(item => item.value).length;
});

const updateColumnVisibility = (item, newValue) => {
  const stateBefore = itemsRef.value.map(item => ({ 
    name: item.name, 
    value: item.value 
  }))
  
  if (props.isSingle) {
    handleSingleSelectionMode(item, newValue)
  } else {
    handleMultipleSelectionMode(item, newValue)
  }
  
  const somethingChanged = itemsRef.value.some((currentItem, index) => {
    const previousItem = stateBefore[index]
    return currentItem.value !== previousItem.value
  })
  
  // Only emit if something actually changed
  if (somethingChanged) {
    emit(props.emitEvent, toRaw(itemsRef.value))
  }
}

const handleSingleSelectionMode = (targetItem, shouldActivate) => {
  if (shouldActivate) {
    handleActivationInSingleMode(targetItem)
  } else {
    handleDeactivationInSingleMode(targetItem)
  }
}

const handleActivationInSingleMode = (targetItem) => {
  if (targetItem.value === true) {
    return
  }
  
  itemsRef.value.forEach(item => {
    item.value = false
  })
  
  targetItem.value = true
}

const handleDeactivationInSingleMode = (targetItem) => {
  const activeItems = itemsRef.value.filter(item => item.value === true)
  const numberOfActiveItems = activeItems.length
  
  if (numberOfActiveItems > 1) {
    targetItem.value = false
  } else {
    targetItem.value = true
  }
}

const handleMultipleSelectionMode = (targetItem, newValue) => {
  targetItem.value = newValue
}

function handleClickOutside(event) {
  if (!event.target.closest('.dropdown-container')) {
    dropdownOpen.value = false;
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
/* Red theme matching ECG */
:root {
  --highlights: #7b181c;
  --highlights-background: #95464917;
}

.dropdown-container {
  position: relative;
  display: inline-block;
  min-width: 150px;
}

.dropdown-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-radius: 12px;
  cursor: pointer;
  user-select: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
  border: 2px solid transparent;
  position: relative;
  overflow: hidden;
}

.dropdown-trigger::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.dropdown-trigger:hover::before {
  opacity: 1;
}


.dropdown-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 14px;
}

.label-text {
  font-weight: 600;
}

.selected-count {
  background: rgba(255, 255, 255, 0.2);
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  backdrop-filter: blur(10px);
}

.arrow-container {
  display: flex;
  align-items: center;
}

.arrow {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  color: rgba(0, 0, 0, 0.8);
}

.arrow.open {
  transform: rotate(180deg);
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1), 0 8px 16px rgba(0, 0, 0, 0.08);
  z-index: 1000;
  border: 1px solid rgba(0, 0, 0, 0.05);
  overflow: hidden;
  backdrop-filter: blur(20px);
  background: rgba(255, 255, 255, 0.95);
}


.dropdown-items {
  max-height: 280px;
  overflow-y: auto;
  padding: 8px 0;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-left: 3px solid transparent;
  position: relative;
}

.dropdown-item:hover {
  background: var(--highlights-hover, #7b181c40);
  border-left-color: var(--highlights, #7b181c);
}

.dropdown-item.checked {
  background: var(--highlights-background, #95464917);
  border-left-color: var(--highlights, #7b181c);
}

.checkbox-container {
  position: relative;
  display: flex;
  align-items: center;
}

.checkbox-input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
}

.custom-checkbox {
  width: 18px;
  height: 18px;
  border: 2px solid #d1d5db;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  background: white;
}

.dropdown-item:hover .custom-checkbox {
  border-color: rgba(123, 24, 28, 0.3);
}

.dropdown-item.checked .custom-checkbox {
  background: var(--highlights, #7b181c);
  border-color: var(--highlights, #7b181c);
}

.checkmark {
  opacity: 0;
  transform: scale(0.5);
  transition: all 0.2s ease;
}

.dropdown-item.checked .checkmark {
  opacity: 1;
  transform: scale(1);
}

.item-text {
  color: #374151;
  font-size: 14px;
  font-weight: 500;
  flex: 1;
}

.dropdown-item.checked .item-text {
  color: #1f2937;
}

.dropdown-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
  background: transparent;
}

.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.dropdown-enter-from {
  opacity: 0;
  transform: translateY(-8px) scale(0.95);
}

.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.95);
}

.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px 20px;
  color: #9ca3af;
}

.loading-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
}

.spinner {
  animation: spin 1s linear infinite;
  color: #667eea;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Fade animation for loading state */
.loading-container {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>